import { useEffect } from 'react'
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
} from 'recharts'
import { useTheme } from '@/lib/theme'

// Mock data - will be replaced with real data from Supabase
const MOCK_REVENUE_DATA = [
  { name: 'Jan', value: 4000, value2: 2400 },
  { name: 'Feb', value: 3000, value2: 1398 },
  { name: 'Mar', value: 2000, value2: 9800 },
  { name: 'Apr', value: 2780, value2: 3908 },
  { name: 'May', value: 1890, value2: 4800 },
  { name: 'Jun', value: 2390, value2: 3800 },
  { name: 'Jul', value: 3490, value2: 4300 },
  { name: 'Aug', value: 4200, value2: 5100 },
  { name: 'Sep', value: 3800, value2: 4600 },
  { name: 'Oct', value: 5100, value2: 6100 },
  { name: 'Nov', value: 4800, value2: 5800 },
  { name: 'Dec', value: 6200, value2: 7200 },
]

interface ChartData {
  name: string
  value: number
  value2?: number
}

interface RevenueAreaChartProps {
  data?: ChartData[]
}

export function RevenueAreaChart({ data = MOCK_REVENUE_DATA }: RevenueAreaChartProps) {
  const { currentTheme } = useTheme()

  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={currentTheme.colors.primary} stopOpacity={0.3} />
            <stop offset="95%" stopColor={currentTheme.colors.primary} stopOpacity={0} />
          </linearGradient>
          <linearGradient id="colorValue2" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
            <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke={currentTheme.colors.border} vertical={false} />
        <XAxis
          dataKey="name"
          stroke={currentTheme.colors.textMuted}
          fontSize={12}
          tickLine={false}
          axisLine={false}
        />
        <YAxis
          stroke={currentTheme.colors.textMuted}
          fontSize={12}
          tickLine={false}
          axisLine={false}
          tickFormatter={(value) => `$${value}`}
        />
        <Tooltip
          contentStyle={{
            backgroundColor: currentTheme.colors.bgCard,
            borderColor: currentTheme.colors.border,
            color: currentTheme.colors.textMain,
          }}
          itemStyle={{ color: currentTheme.colors.textMain }}
        />
        <Area
          type="monotone"
          dataKey="value"
          stroke={currentTheme.colors.primary}
          strokeWidth={2}
          fillOpacity={1}
          fill="url(#colorValue)"
          name="Revenue"
        />
        <Area
          type="monotone"
          dataKey="value2"
          stroke="#10b981"
          strokeWidth={2}
          fillOpacity={1}
          fill="url(#colorValue2)"
          name="Profit"
        />
      </AreaChart>
    </ResponsiveContainer>
  )
}

interface ActivityBarChartProps {
  data?: ChartData[]
}

export function ActivityBarChart({ data = MOCK_REVENUE_DATA }: ActivityBarChartProps) {
  const { currentTheme } = useTheme()

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={currentTheme.colors.border} vertical={false} />
        <XAxis
          dataKey="name"
          stroke={currentTheme.colors.textMuted}
          fontSize={12}
          tickLine={false}
          axisLine={false}
        />
        <YAxis stroke={currentTheme.colors.textMuted} fontSize={12} tickLine={false} axisLine={false} />
        <Tooltip
          cursor={{ fill: currentTheme.colors.bgElement, opacity: 0.5 }}
          contentStyle={{
            backgroundColor: currentTheme.colors.bgCard,
            borderColor: currentTheme.colors.border,
            color: currentTheme.colors.textMain,
          }}
        />
        <Legend />
        <Bar dataKey="value" fill={currentTheme.colors.primary} name="New Users" radius={[4, 4, 0, 0]} />
        <Bar dataKey="value2" fill={currentTheme.colors.bgHover} name="Active Users" radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  )
}

interface MetricLineChartProps {
  data?: ChartData[]
  dataKey?: string
  name?: string
}

export function MetricLineChart({ data = MOCK_REVENUE_DATA, dataKey = 'value', name = 'Value' }: MetricLineChartProps) {
  const { currentTheme } = useTheme()

  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={data} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={currentTheme.colors.border} vertical={false} />
        <XAxis
          dataKey="name"
          stroke={currentTheme.colors.textMuted}
          fontSize={12}
          tickLine={false}
          axisLine={false}
        />
        <YAxis stroke={currentTheme.colors.textMuted} fontSize={12} tickLine={false} axisLine={false} />
        <Tooltip
          contentStyle={{
            backgroundColor: currentTheme.colors.bgCard,
            borderColor: currentTheme.colors.border,
            color: currentTheme.colors.textMain,
          }}
        />
        <Line
          type="monotone"
          dataKey={dataKey}
          stroke={currentTheme.colors.primary}
          strokeWidth={2}
          dot={{ fill: currentTheme.colors.primary, strokeWidth: 0, r: 4 }}
          activeDot={{ r: 6 }}
          name={name}
        />
      </LineChart>
    </ResponsiveContainer>
  )
}

// MRR Trend Chart Component
interface MRRTrendData {
  month: string
  mrr: number
  netMrr: number
  monthly: number
  yearly: number
}

interface MRRTrendChartProps {
  data: MRRTrendData[]
}

export function MRRTrendChart({ data }: MRRTrendChartProps) {
  const { currentTheme } = useTheme()

  if (!data || data.length === 0) {
    return (
      <div className="h-full flex items-center justify-center text-[var(--text-muted)]">
        No revenue data available
      </div>
    )
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id="colorMRR" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
            <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
          </linearGradient>
          <linearGradient id="colorNetMRR" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={currentTheme.colors.primary} stopOpacity={0.3} />
            <stop offset="95%" stopColor={currentTheme.colors.primary} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke={currentTheme.colors.border} vertical={false} />
        <XAxis
          dataKey="month"
          stroke={currentTheme.colors.textMuted}
          fontSize={12}
          tickLine={false}
          axisLine={false}
        />
        <YAxis
          stroke={currentTheme.colors.textMuted}
          fontSize={12}
          tickLine={false}
          axisLine={false}
          tickFormatter={(value) => `$${value.toLocaleString()}`}
        />
        <Tooltip
          contentStyle={{
            backgroundColor: currentTheme.colors.bgCard,
            borderColor: currentTheme.colors.border,
            color: currentTheme.colors.textMain,
          }}
          itemStyle={{ color: currentTheme.colors.textMain }}
          formatter={(value: number | undefined, name?: string) => [
            `$${(value ?? 0).toLocaleString()}`,
            name === 'mrr' ? 'Gross MRR' : 'Net MRR (after 15% fees)',
          ]}
          labelFormatter={(label) => `Month: ${label}`}
        />
        <Legend
          verticalAlign="top"
          height={36}
          formatter={(value) => (
            <span style={{ color: currentTheme.colors.textMuted }}>
              {value === 'mrr' ? 'Gross MRR' : 'Net MRR'}
            </span>
          )}
        />
        <Area
          type="monotone"
          dataKey="mrr"
          stroke="#10b981"
          strokeWidth={2}
          fillOpacity={1}
          fill="url(#colorMRR)"
          name="mrr"
        />
        <Area
          type="monotone"
          dataKey="netMrr"
          stroke={currentTheme.colors.primary}
          strokeWidth={2}
          fillOpacity={1}
          fill="url(#colorNetMRR)"
          name="netMrr"
        />
      </AreaChart>
    </ResponsiveContainer>
  )
}

// User Growth Chart Component
interface UserGrowthData {
  date: string
  users: number
  cumulative: number
}

interface UserGrowthChartProps {
  data: UserGrowthData[]
}

export function UserGrowthChart({ data }: UserGrowthChartProps) {
  const { currentTheme } = useTheme()

  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id="colorCumulative" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={currentTheme.colors.primary} stopOpacity={0.3} />
            <stop offset="95%" stopColor={currentTheme.colors.primary} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke={currentTheme.colors.border} vertical={false} />
        <XAxis
          dataKey="date"
          stroke={currentTheme.colors.textMuted}
          fontSize={12}
          tickLine={false}
          axisLine={false}
        />
        <YAxis
          stroke={currentTheme.colors.textMuted}
          fontSize={12}
          tickLine={false}
          axisLine={false}
          tickFormatter={(value) => value.toLocaleString()}
        />
        <Tooltip
          contentStyle={{
            backgroundColor: currentTheme.colors.bgCard,
            borderColor: currentTheme.colors.border,
            color: currentTheme.colors.textMain,
          }}
          itemStyle={{ color: currentTheme.colors.textMain }}
          formatter={(value, name) => [
            typeof value === 'number' ? value.toLocaleString() : String(value),
            name === 'cumulative' ? 'Total Users' : 'New Users',
          ]}
        />
        <Area
          type="monotone"
          dataKey="cumulative"
          stroke={currentTheme.colors.primary}
          strokeWidth={2}
          fillOpacity={1}
          fill="url(#colorCumulative)"
          name="cumulative"
        />
      </AreaChart>
    </ResponsiveContainer>
  )
}

interface PieChartData {
  name: string
  value: number
  [key: string]: string | number
}

interface OrgPieChartProps {
  data?: PieChartData[]
}

const PIE_COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6']

// Subscriber Distribution Pie Chart
interface SubscriberPieChartProps {
  monthly: number
  yearly: number
}

export function SubscriberPieChart({ monthly, yearly }: SubscriberPieChartProps) {
  const { currentTheme } = useTheme()

  const data = [
    { name: 'Monthly', value: monthly, color: '#3b82f6' },
    { name: 'Yearly', value: yearly, color: '#10b981' },
  ]

  const total = monthly + yearly

  return (
    <ResponsiveContainer width="100%" height="100%">
      <PieChart>
        <Pie
          data={data}
          cx="50%"
          cy="50%"
          innerRadius={50}
          outerRadius={70}
          paddingAngle={3}
          dataKey="value"
        >
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={entry.color} />
          ))}
        </Pie>
        <Tooltip
          contentStyle={{
            backgroundColor: currentTheme.colors.bgCard,
            borderColor: currentTheme.colors.border,
            color: currentTheme.colors.textMain,
          }}
          formatter={(value: number | undefined, name?: string) => [
            `${(value ?? 0).toLocaleString()} (${total > 0 ? (((value ?? 0) / total) * 100).toFixed(1) : 0}%)`,
            name ?? '',
          ]}
        />
        <Legend
          verticalAlign="bottom"
          height={36}
          formatter={(value) => <span style={{ color: currentTheme.colors.textMuted }}>{value}</span>}
        />
      </PieChart>
    </ResponsiveContainer>
  )
}

// ARR Projection Line Chart
interface ARRProjectionData {
  month: string
  arr: number
  projected?: boolean
}

interface ARRProjectionChartProps {
  data: ARRProjectionData[]
}

export function ARRProjectionChart({ data }: ARRProjectionChartProps) {
  const { currentTheme } = useTheme()

  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id="colorARR" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
            <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke={currentTheme.colors.border} vertical={false} />
        <XAxis
          dataKey="month"
          stroke={currentTheme.colors.textMuted}
          fontSize={12}
          tickLine={false}
          axisLine={false}
        />
        <YAxis
          stroke={currentTheme.colors.textMuted}
          fontSize={12}
          tickLine={false}
          axisLine={false}
          tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
        />
        <Tooltip
          contentStyle={{
            backgroundColor: currentTheme.colors.bgCard,
            borderColor: currentTheme.colors.border,
            color: currentTheme.colors.textMain,
          }}
          formatter={(value: number | undefined) => [`$${(value ?? 0).toLocaleString()}`, 'ARR']}
        />
        <Area
          type="monotone"
          dataKey="arr"
          stroke="#10b981"
          strokeWidth={2}
          fillOpacity={1}
          fill="url(#colorARR)"
        />
      </AreaChart>
    </ResponsiveContainer>
  )
}

// Recent Signups Bar Chart
interface SignupBarData {
  period: string
  signups: number
}

interface SignupBarChartProps {
  data: SignupBarData[]
}

export function SignupBarChart({ data }: SignupBarChartProps) {
  const { currentTheme } = useTheme()

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={currentTheme.colors.border} vertical={false} />
        <XAxis
          dataKey="period"
          stroke={currentTheme.colors.textMuted}
          fontSize={11}
          tickLine={false}
          axisLine={false}
        />
        <YAxis
          stroke={currentTheme.colors.textMuted}
          fontSize={11}
          tickLine={false}
          axisLine={false}
        />
        <Tooltip
          cursor={{ fill: currentTheme.colors.bgElement, opacity: 0.5 }}
          contentStyle={{
            backgroundColor: currentTheme.colors.bgCard,
            borderColor: currentTheme.colors.border,
            color: currentTheme.colors.textMain,
          }}
        />
        <Bar dataKey="signups" fill={currentTheme.colors.primary} name="New Users" radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  )
}

// Conversion Funnel Chart
interface FunnelData {
  name: string
  value: number
  fill: string
}

interface ConversionFunnelChartProps {
  total: number
  premium: number
}

export function ConversionFunnelChart({ total, premium }: ConversionFunnelChartProps) {
  const { currentTheme } = useTheme()
  const free = total - premium

  const data = [
    { name: 'Total Users', value: total, fill: currentTheme.colors.primary },
    { name: 'Free Users', value: free, fill: '#64748b' },
    { name: 'Premium Users', value: premium, fill: '#f59e0b' },
  ]

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} layout="vertical" margin={{ top: 10, right: 30, left: 80, bottom: 10 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={currentTheme.colors.border} horizontal={false} />
        <XAxis
          type="number"
          stroke={currentTheme.colors.textMuted}
          fontSize={11}
          tickLine={false}
          axisLine={false}
          tickFormatter={(value) => value.toLocaleString()}
        />
        <YAxis
          type="category"
          dataKey="name"
          stroke={currentTheme.colors.textMuted}
          fontSize={11}
          tickLine={false}
          axisLine={false}
        />
        <Tooltip
          cursor={{ fill: currentTheme.colors.bgElement, opacity: 0.3 }}
          contentStyle={{
            backgroundColor: currentTheme.colors.bgCard,
            borderColor: currentTheme.colors.border,
            color: currentTheme.colors.textMain,
          }}
          formatter={(value: number | undefined) => [(value ?? 0).toLocaleString(), 'Users']}
        />
        <Bar dataKey="value" radius={[0, 4, 4, 0]}>
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={entry.fill} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  )
}

export function OrgPieChart({ data }: OrgPieChartProps) {
  const { currentTheme } = useTheme()

  const defaultData = [
    { name: "Buy'r", value: 35 },
    { name: 'Plantr', value: 25 },
    { name: 'Webb', value: 20 },
    { name: 'WebApp', value: 12 },
    { name: 'StoryMind', value: 8 },
  ]

  const chartData = data || defaultData

  return (
    <ResponsiveContainer width="100%" height="100%">
      <PieChart>
        <Pie
          data={chartData}
          cx="50%"
          cy="50%"
          innerRadius={60}
          outerRadius={80}
          paddingAngle={5}
          dataKey="value"
        >
          {chartData.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
          ))}
        </Pie>
        <Tooltip
          contentStyle={{
            backgroundColor: currentTheme.colors.bgCard,
            borderColor: currentTheme.colors.border,
            color: currentTheme.colors.textMain,
          }}
        />
        <Legend
          verticalAlign="bottom"
          height={36}
          formatter={(value) => <span style={{ color: currentTheme.colors.textMuted }}>{value}</span>}
        />
      </PieChart>
    </ResponsiveContainer>
  )
}
